import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button, Field, Spinner } from "./ui/kit";
import { VyberVyrobce, VyberModel, useCenikVyber } from "./VyberZCeniku";
import api, { apiError } from "../lib/api";
import Vyber from "./Vyber";

/**
 * Servisní požadavek: klient volá, že mu nejde kotel. Jde mimo obchod —
 * žádný případ ani nabídka. Vybere se klient a jeho zařízení (nebo se
 * založí nové, které jsme nemontovali), termín a kdo pojede. Vznikne
 * aktivita v kalendáři; na místě se píše zápis typu Oprava.
 */
export default function NovyPozadavekDialog({ clientId = null, onClose }) {
  const nav = useNavigate();
  const [clients, setClients] = useState(null);
  const [cid, setCid] = useState(clientId || "");
  const [zarizeni, setZarizeni] = useState([]);
  const [zid, setZid] = useState("");
  const [nove, setNove] = useState(false);
  const [kats, setKats] = useState([]);
  const { vyrobci, zarizeni: modely } = useCenikVyber();
  const [nz, setNz] = useState({ nazev: "", vyrobce: "", vyrobce_id: null, polozka_id: null, kategorie_id: "" });
  const [popis, setPopis] = useState("");
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));
  const [members, setMembers] = useState([]);
  const [osoby, setOsoby] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clientId) {
      api.get("/api/clients", { params: { limit: 200 } })
        .then(({ data }) => setClients(data.items || [])).catch(() => setClients([]));
    }
    api.get("/api/meta/ciselniky").then((r) => setKats(r.data.kategorie || [])).catch(() => {});
    api.get("/api/team")
      .then(({ data }) => setMembers((data.members || []).filter((m) => m.active !== false)))
      .catch(() => setMembers([]));
  }, [clientId]);

  useEffect(() => {
    if (!cid) { setZarizeni([]); setZid(""); return; }
    api.get("/api/zarizeni", { params: { client_id: cid, limit: 100 } })
      .then(({ data }) => {
        const items = data.items || [];
        setZarizeni(items);
        setZid(items[0]?.id || "");
        setNove(items.length === 0);
      })
      .catch(() => { setZarizeni([]); setNove(true); });
  }, [cid]);

  const toggleOsoba = (id) => setOsoby((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!cid) return setError("Vyber klienta.");
    if (nove && !nz.vyrobce_id) return setError("Vyber výrobce z ceníku.");
    if (nove && !nz.polozka_id) return setError("Vyber model z ceníku.");
    if (nove && !nz.nazev.trim()) return setError("Zadej název zařízení.");
    if (!nove && !zid) return setError("Vyber zařízení, nebo zadej nové.");
    setError(""); setSaving(true);
    try {
      const { data } = await api.post("/api/servis/pozadavky", {
        client_id: cid,
        zarizeni_id: nove ? null : zid,
        nove_zarizeni: nove ? { ...nz, kategorie_id: nz.kategorie_id || null } : null,
        popis, datum, odpovedne_osoby: osoby,
      });
      nav(`/servis/zarizeni/${data.zarizeni_id}`);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="pozadavek-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[480px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-line-softer px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-[16px] font-800 text-ink">Nový servisní požadavek</h2>
            <div className="t-meta text-muted">Oprava bez nabídky a obchodního případu.</div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink" data-testid="pozadavek-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {!clientId && (
            <Field label="Klient">
              {clients === null ? <div className="py-2"><Spinner /></div> : (
                <Vyber className="field" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="pozadavek-klient">
                  <option value="">Vyber klienta…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
                </Vyber>
              )}
            </Field>
          )}
          {cid && (
            <Field label="Zařízení">
              {zarizeni.length > 0 && (
                <Vyber className="field mb-2" value={nove ? "" : zid} disabled={nove}
                  onChange={(e) => setZid(e.target.value)} data-testid="pozadavek-zarizeni">
                  {zarizeni.map((z) => <option key={z.id} value={z.id}>{z.nazev}{z.vyrobce ? ` · ${z.vyrobce}` : ""}</option>)}
                </Vyber>
              )}
              <label className="flex items-center gap-2 t-telo text-sec cursor-pointer">
                <input type="checkbox" checked={nove} onChange={(e) => setNove(e.target.checked)} data-testid="pozadavek-nove" />
                {zarizeni.length ? "Jiné zařízení, které jsme nemontovali" : "Zařízení ještě nemáme — založit nové"}
              </label>
              {nove && (
                <div className="mt-3 space-y-3">
                  <Vyber className="field" value={nz.kategorie_id} data-testid="pozadavek-kategorie"
                    onChange={(e) => setNz({ ...nz, kategorie_id: e.target.value, vyrobce_id: null, vyrobce: "", polozka_id: null })}>
                    <option value="">Technologie…</option>
                    {kats.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </Vyber>
                  {/* Výrobce i model jen z ceníku — cizí kotel se do ceníku doplní jako zařízení. */}
                  <VyberVyrobce vyrobci={vyrobci} kategorieId={nz.kategorie_id} vyrobceId={nz.vyrobce_id}
                    onChange={({ vyrobce_id, vyrobce }) => setNz({ ...nz, vyrobce_id, vyrobce, polozka_id: null })}
                    testid="pozadavek-vyrobce" />
                  <VyberModel zarizeni={modely} vyrobceId={nz.vyrobce_id} kategorieId={nz.kategorie_id} polozkaId={nz.polozka_id}
                    onChange={(z) => setNz({ ...nz, polozka_id: z?.id || null, nazev: nz.nazev || z?.name || "",
                      kategorie_id: nz.kategorie_id || z?.kategorie_id || "" })}
                    testid="pozadavek-model" />
                  <input className="field" value={nz.nazev} onChange={(e) => setNz({ ...nz, nazev: e.target.value })}
                    placeholder="Název zařízení (předvyplní se z modelu)" data-testid="pozadavek-nazev" />
                </div>
              )}
            </Field>
          )}
          <Field label="Co se děje">
            <textarea className="field min-h-[72px]" value={popis} onChange={(e) => setPopis(e.target.value)}
              placeholder="Netopí, hlásí chybu E12…" data-testid="pozadavek-popis" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Termín výjezdu">
              <input type="date" className="field" value={datum} onChange={(e) => setDatum(e.target.value)} data-testid="pozadavek-datum" />
            </Field>
          </div>
          <Field label="Kdo pojede">
            <div className="flex flex-wrap gap-2">
              {members.map((m) => (
                <button key={m.id} type="button" onClick={() => toggleOsoba(m.id)}
                  className={`pill px-3 h-8 text-[12.5px] font-semibold border ${
                    osoby.includes(m.id) ? "bg-accent text-white border-accent"
                      : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
                  {m.name}
                </button>
              ))}
            </div>
            <p className="t-meta text-muted mt-1.5">Když nikoho nevybereš, zůstane to na tobě.</p>
          </Field>
          {error && <div className="text-[12.5px] text-danger" data-testid="pozadavek-error">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose}>Zrušit</Button>
            <Button type="submit" disabled={saving} data-testid="pozadavek-submit">{saving ? <Spinner /> : "Založit požadavek"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
